import { useState, useEffect, useCallback } from "react";
import {
  competitors as api,
  type CompetitorTrack,
  type CompetitorEmail,
  type CompetitorAnalysis,
} from "@/lib/api";
import Spinner from "@/components/Spinner";
import {
  Eye, Plus, Trash2, X, Mail, Calendar, Globe,
  BarChart3, Clock, Link2, Image, ChevronRight,
  Search, AlertTriangle, TrendingUp, Target,
  FileText, ArrowLeft, ExternalLink, Type,
} from "lucide-react";

type View = "list" | "emails" | "email-detail" | "analysis";

export default function CompetitorTrackerPage() {
  const [view, setView] = useState<View>("list");
  const [tracks, setTracks] = useState<CompetitorTrack[]>([]);
  const [loading, setLoading] = useState(true);

  // Add competitor
  const [showAdd, setShowAdd] = useState(false);
  const [addName, setAddName] = useState("");
  const [addDomain, setAddDomain] = useState("");
  const [addEmail, setAddEmail] = useState("");
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // Emails view
  const [selectedTrack, setSelectedTrack] = useState<CompetitorTrack | null>(null);
  const [emails, setEmails] = useState<CompetitorEmail[]>([]);
  const [emailsLoading, setEmailsLoading] = useState(false);

  // Email detail view
  const [selectedEmail, setSelectedEmail] = useState<CompetitorEmail | null>(null);

  // Analysis view
  const [analysis, setAnalysis] = useState<CompetitorAnalysis | null>(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);

  const loadTracks = useCallback(async () => {
    try {
      const res = await api.list();
      setTracks(res.competitors);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadTracks(); }, [loadTracks]);

  async function handleAdd() {
    if (!addName.trim() || !addDomain.trim()) { setAddError("Name and domain are required"); return; }
    setAdding(true); setAddError(null);
    try {
      await api.add({
        competitorName: addName.trim(),
        competitorDomain: addDomain.trim(),
        monitoringEmail: addEmail.trim() || undefined,
      });
      setShowAdd(false); setAddName(""); setAddDomain(""); setAddEmail("");
      loadTracks();
    } catch (e: any) { setAddError(e.message || "Failed to add"); }
    finally { setAdding(false); }
  }

  async function handleRemove(id: string) {
    if (!confirm("Remove this competitor?")) return;
    try { await api.remove(id); loadTracks(); }
    catch (e) { console.error(e); }
  }

  async function viewEmails(track: CompetitorTrack) {
    setSelectedTrack(track);
    setEmailsLoading(true);
    setView("emails");
    try {
      const res = await api.emails(track.id);
      setEmails(res.emails);
    } catch (e) { console.error(e); }
    finally { setEmailsLoading(false); }
  }

  async function viewEmailDetail(track: CompetitorTrack, emailId: string) {
    try {
      const res = await api.email(track.id, emailId);
      setSelectedEmail(res.email);
      setView("email-detail");
    } catch (e) { console.error(e); }
  }

  async function viewAnalysis() {
    setAnalysisLoading(true);
    setView("analysis");
    try {
      const res = await api.analysis();
      setAnalysis(res);
    } catch (e) { console.error(e); }
    finally { setAnalysisLoading(false); }
  }

  if (loading) return <div className="max-w-5xl mx-auto px-4 py-20 flex justify-center"><Spinner /></div>;

  // ── Email Detail View ──
  if (view === "email-detail" && selectedEmail && selectedTrack) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button onClick={() => { setView("emails"); setSelectedEmail(null); }}
          className="flex items-center gap-1.5 text-sm text-slate hover:text-ink mb-6">
          <ArrowLeft size={15} /> Back to Emails
        </button>
        <div className="bg-white rounded-xl border border-sand p-6">
          <div className="mb-4 pb-4 border-b border-sand">
            <p className="text-xs text-slate mb-1">From: <span className="text-ink">{selectedEmail.from_address}</span></p>
            <h2 className="font-display font-bold text-ink text-lg">{selectedEmail.subject || "(no subject)"}</h2>
            {selectedEmail.preview_text && <p className="text-xs text-slate mt-1">Preview: {selectedEmail.preview_text}</p>}
            <div className="flex gap-4 mt-2 text-[11px] text-slate">
              <span><Calendar size={10} className="inline" /> {new Date(selectedEmail.received_at).toLocaleString()}</span>
              <span><Type size={10} className="inline" /> {selectedEmail.word_count} words</span>
              <span><Link2 size={10} className="inline" /> {selectedEmail.link_count} links</span>
              {selectedEmail.cta_text && <span><Target size={10} className="inline" /> CTA: "{selectedEmail.cta_text}"</span>}
            </div>
          </div>
          {selectedEmail.body_html ? (
            <div className="bg-paper rounded-lg p-4 border border-sand/60">
              <iframe
                srcDoc={selectedEmail.body_html}
                sandbox="allow-same-origin"
                className="w-full border-0 min-h-[500px] rounded"
                title="Email preview"
              />
            </div>
          ) : selectedEmail.body_text ? (
            <div className="bg-paper rounded-lg p-4 border border-sand/60">
              <pre className="text-xs text-slate whitespace-pre-wrap font-body">{selectedEmail.body_text}</pre>
            </div>
          ) : (
            <p className="text-sm text-slate">No content available.</p>
          )}
        </div>
      </div>
    );
  }

  // ── Analysis View ──
  if (view === "analysis") {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button onClick={() => setView("list")}
          className="flex items-center gap-1.5 text-sm text-slate hover:text-ink mb-6">
          <ArrowLeft size={15} /> Back to Competitors
        </button>
        <h1 className="text-2xl font-display font-bold text-ink flex items-center gap-2 mb-6">
          <BarChart3 size={22} className="text-coral" /> Competitive Analysis
        </h1>

        {analysisLoading ? <Spinner /> : !analysis ? (
          <p className="text-sm text-slate">No data available.</p>
        ) : (
          <div className="space-y-6">
            {/* Per-competitor stats */}
            <div className="bg-white rounded-xl border border-sand p-5">
              <h3 className="text-sm font-bold text-ink mb-3">Competitor Comparison</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-sand">
                    <th className="text-left py-2 px-2 text-xs font-semibold text-slate">Competitor</th>
                    <th className="text-right py-2 px-2 text-xs font-semibold text-slate">Emails</th>
                    <th className="text-right py-2 px-2 text-xs font-semibold text-slate">Last 30d</th>
                    <th className="text-right py-2 px-2 text-xs font-semibold text-slate">Avg Words</th>
                    <th className="text-right py-2 px-2 text-xs font-semibold text-slate">Avg Links</th>
                  </tr></thead>
                  <tbody>
                    {analysis.competitors.map(c => (
                      <tr key={c.id} className="border-b border-sand/50">
                        <td className="py-2 px-2 text-xs font-medium text-ink">{c.competitor_name}</td>
                        <td className="py-2 px-2 text-xs text-slate text-right">{c.email_count}</td>
                        <td className="py-2 px-2 text-xs text-slate text-right">{c.emails_last_30d}</td>
                        <td className="py-2 px-2 text-xs text-slate text-right">{c.avg_word_count}</td>
                        <td className="py-2 px-2 text-xs text-slate text-right">{c.avg_links}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Send day patterns */}
            {analysis.patterns.byDayOfWeek.length > 0 && (
              <div className="bg-white rounded-xl border border-sand p-5">
                <h3 className="text-sm font-bold text-ink mb-3">When Competitors Send (Day of Week)</h3>
                <div className="flex items-end gap-2 h-32">
                  {analysis.patterns.byDayOfWeek.map(d => {
                    const max = Math.max(...analysis.patterns.byDayOfWeek.map(x => x.count), 1);
                    return (
                      <div key={d.day} className="flex-1 flex flex-col items-center justify-end h-full">
                        <div className="w-full bg-coral/70 rounded-t transition-all"
                          style={{ height: `${(d.count / max) * 100}%`, minHeight: "4px" }} />
                        <span className="text-[9px] text-slate mt-1">{d.day}</span>
                        <span className="text-[9px] text-slate/50">{d.count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Subject line stats */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white rounded-xl border border-sand p-4 text-center">
                <p className="font-display font-bold text-ink text-xl">{analysis.subjectLineStats.avgLength}</p>
                <p className="text-[10px] text-slate">Avg Subject Length</p>
              </div>
              <div className="bg-white rounded-xl border border-sand p-4 text-center">
                <p className="font-display font-bold text-ink text-xl">{analysis.subjectLineStats.minLength}</p>
                <p className="text-[10px] text-slate">Shortest Subject</p>
              </div>
              <div className="bg-white rounded-xl border border-sand p-4 text-center">
                <p className="font-display font-bold text-ink text-xl">{analysis.subjectLineStats.maxLength}</p>
                <p className="text-[10px] text-slate">Longest Subject</p>
              </div>
            </div>

            {/* Top CTAs */}
            {analysis.topCtas.length > 0 && (
              <div className="bg-white rounded-xl border border-sand p-5">
                <h3 className="text-sm font-bold text-ink mb-3">Most Used CTAs by Competitors</h3>
                <div className="space-y-2">
                  {analysis.topCtas.map((cta, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="w-6 h-6 bg-coral/10 text-coral rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0">{i + 1}</span>
                      <span className="text-xs text-ink font-medium flex-1">"{cta.text}"</span>
                      <span className="text-[10px] text-slate">{cta.count}x</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // ── Emails View ──
  if (view === "emails" && selectedTrack) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button onClick={() => { setView("list"); setSelectedTrack(null); }}
          className="flex items-center gap-1.5 text-sm text-slate hover:text-ink mb-6">
          <ArrowLeft size={15} /> Back to Competitors
        </button>
        <h1 className="text-xl font-display font-bold text-ink mb-1">{selectedTrack.competitor_name}</h1>
        <p className="text-xs text-slate mb-6">{selectedTrack.competitor_domain} — {selectedTrack.email_count} emails captured</p>

        {emailsLoading ? <Spinner /> : emails.length === 0 ? (
          <div className="bg-white rounded-xl border border-sand py-12 text-center">
            <Mail size={32} className="mx-auto text-slate/20 mb-3" />
            <p className="text-sm text-slate">No emails captured yet. Emails will appear here as they arrive.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {emails.map(email => (
              <button key={email.id} onClick={() => viewEmailDetail(selectedTrack, email.id)}
                className="w-full bg-white rounded-xl border border-sand p-4 text-left hover:border-coral/30 hover:shadow-sm transition-all flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-ink truncate">{email.subject || "(no subject)"}</p>
                  <div className="flex items-center gap-3 mt-1 text-[11px] text-slate">
                    <span><Calendar size={10} className="inline" /> {new Date(email.received_at).toLocaleDateString()}</span>
                    <span><Type size={10} className="inline" /> {email.word_count}w</span>
                    <span><Link2 size={10} className="inline" /> {email.link_count} links</span>
                    {email.has_images === 1 && <span><Image size={10} className="inline" /> images</span>}
                    {email.cta_text && <span className="text-coral truncate max-w-[120px]">CTA: {email.cta_text}</span>}
                  </div>
                </div>
                <ChevronRight size={14} className="text-slate flex-shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── List View (default) ──
  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display font-bold text-ink flex items-center gap-2">
            <Eye size={22} className="text-coral" /> Competitor Tracker
          </h1>
          <p className="text-slate text-sm mt-0.5">Monitor what your competitors are sending.</p>
        </div>
        <div className="flex items-center gap-2">
          {tracks.length > 0 && (
            <button onClick={viewAnalysis}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-sand text-sm font-medium text-slate hover:bg-paper transition-colors">
              <BarChart3 size={14} /> Analysis
            </button>
          )}
          <button onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-coral hover:bg-coral-dark text-white text-sm font-semibold transition-colors">
            <Plus size={15} /> Add Competitor
          </button>
        </div>
      </div>

      {/* Add modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowAdd(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <h2 className="font-display font-bold text-lg text-ink mb-4">Track a Competitor</h2>
            <div className="space-y-3 mb-5">
              <div>
                <label className="block text-xs font-semibold text-ink mb-1">Competitor Name *</label>
                <input type="text" value={addName} onChange={e => setAddName(e.target.value)}
                  placeholder="e.g. Mailchimp"
                  className="w-full px-3 py-2 rounded-lg border border-sand text-sm focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-ink mb-1">Their Email Domain *</label>
                <input type="text" value={addDomain} onChange={e => setAddDomain(e.target.value)}
                  placeholder="e.g. mailchimp.com"
                  className="w-full px-3 py-2 rounded-lg border border-sand text-sm focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral" />
                <p className="text-[10px] text-slate mt-0.5">Emails from @this-domain will be captured</p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-ink mb-1">Your Monitoring Email (optional)</label>
                <input type="email" value={addEmail} onChange={e => setAddEmail(e.target.value)}
                  placeholder="e.g. monitor@yourdomain.com"
                  className="w-full px-3 py-2 rounded-lg border border-sand text-sm focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral" />
                <p className="text-[10px] text-slate mt-0.5">Subscribe to their list with this email. We'll capture everything they send.</p>
              </div>
            </div>
            {addError && <p className="text-red-500 text-xs mb-3">{addError}</p>}
            <div className="flex gap-3">
              <button onClick={() => setShowAdd(false)} className="flex-1 px-4 py-2.5 rounded-lg border border-sand text-sm text-slate">Cancel</button>
              <button onClick={handleAdd} disabled={adding}
                className="flex-1 flex items-center justify-center gap-2 bg-coral hover:bg-coral-dark text-white px-4 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50">
                {adding ? "Adding..." : "Track Competitor"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {tracks.length === 0 && (
        <div className="bg-white rounded-xl border border-sand py-16 text-center">
          <Eye size={40} className="mx-auto text-slate/20 mb-3" />
          <h3 className="font-display font-bold text-ink text-lg">No Competitors Tracked</h3>
          <p className="text-sm text-slate mt-1 max-w-sm mx-auto mb-5">
            Add a competitor to start capturing their emails. Subscribe to their newsletter using a monitoring email — we'll analyze everything they send.
          </p>
          <button onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-2 bg-coral hover:bg-coral-dark text-white px-5 py-2.5 rounded-lg text-sm font-semibold">
            <Plus size={14} /> Add Your First Competitor
          </button>
        </div>
      )}

      {/* Competitor cards */}
      {tracks.length > 0 && (
        <div className="space-y-3">
          {tracks.map(track => (
            <div key={track.id} className="bg-white rounded-xl border border-sand p-5 flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-paper flex items-center justify-center flex-shrink-0">
                <Globe size={20} className="text-coral" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-bold text-ink">{track.competitor_name}</h3>
                <p className="text-xs text-slate">{track.competitor_domain}</p>
                <div className="flex items-center gap-3 mt-1 text-[11px] text-slate">
                  <span><Mail size={10} className="inline" /> {track.email_count} emails</span>
                  {track.last_email_at && (
                    <span><Calendar size={10} className="inline" /> Last: {new Date(track.last_email_at).toLocaleDateString()}</span>
                  )}
                </div>
              </div>
              <button onClick={() => viewEmails(track)}
                className="flex items-center gap-1 text-xs font-semibold text-coral hover:underline">
                View Emails <ChevronRight size={12} />
              </button>
              <button onClick={() => handleRemove(track.id)}
                className="p-2 text-slate/40 hover:text-red-500 transition-colors">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
